import {
    clientFieldError,
    type FieldValue,
    isFieldErrorCode,
    type ProviderFieldSchema,
    sameStoredValue,
} from '@/domain/providerConfig';

/** What a field shows when someone else changed it too; only `theirs` is read here. */
export interface ProviderFieldConflict {
    theirs: string | string[] | null;
}

const GENERIC_ERROR_KEY = 'providerConfig.errors.generic';

export function textOf(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

export function listOf(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value.filter((entry): entry is string => typeof entry === 'string');
}

export function toFieldValue(value: unknown): FieldValue {
    if (typeof value === 'string') return value;
    return Array.isArray(value) ? listOf(value) : null;
}

/**
 * The copy key for a form error message. Client rules and a save's 400 set the server's
 * code; other callers may set a copy key. Anything else, a provider's own text included,
 * reads as the generic failure so it is never shown raw.
 */
export function errorKeyOf(message: string | undefined): string {
    if (message === undefined || message === '') return GENERIC_ERROR_KEY;
    if (isFieldErrorCode(message)) return `providerConfig.errors.${message}`;
    return message.startsWith('providerConfig.') ? message : GENERIC_ERROR_KEY;
}

export function conflictText(theirs: ProviderFieldConflict['theirs']): string {
    if (theirs === null) return '';
    return Array.isArray(theirs) ? theirs.join(', ') : theirs;
}

/**
 * A field's own rule: only a value the save writes is judged (R6-1), compared with the
 * loaded one as the server stores both, so a space added to a stored value that breaks a
 * rule, which the save trims and leaves as it is, does not block the save.
 */
export function judgeWritten(
    field: ProviderFieldSchema,
    value: unknown,
    loaded: unknown
): true | string {
    const next = toFieldValue(value);
    if (sameStoredValue(next, toFieldValue(loaded))) return true;
    return clientFieldError(field, next) ?? true;
}
